import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveOrder, generateOTP, saveDeliveryOTP } from '@/lib/supabase';
import { getWalletBalance, updateWalletAfterOrder, calcApplicableBonus, syncWalletFromSupabase } from '@/hooks/useWallet';
import { validateGiftCode, redeemGiftCode, type GiftCode } from '@/lib/supabase';
import { playOrderSuccessSound } from '@/hooks/useOrderSound';
import { normalizePhone } from '@/hooks/useCustomerAuth';
import { trackWithAttribution } from '@/lib/metaPixel';
import type { CartItem, Address, ConfirmType } from '../types';
import type { CatProduct } from '@/mocks/categoryProducts';

interface UseOrderFlowProps {
  cart: CartItem[];
  cartTotal: number;
  savedAddress: Address | null;
  clearCart: () => void;
  setIsCartOpen: (v: boolean) => void;
  customerUserPhone?: string;
}

export function useOrderFlow({
  cart,
  cartTotal,
  savedAddress,
  clearCart,
  setIsCartOpen,
  customerUserPhone,
}: UseOrderFlowProps) {
  const navigate = useNavigate();

  // Wallet
  const [walletBalance, setWalletBalance] = useState(0);
  const [applyBonusCart, setApplyBonusCart] = useState(false);
  const [applyBonusInstant, setApplyBonusInstant] = useState(false);

  // Gift Code - Cart
  const [giftCodeCart, setGiftCodeCart] = useState('');
  const [giftCodeCartData, setGiftCodeCartData] = useState<GiftCode | null>(null);
  const [giftCodeCartDiscount, setGiftCodeCartDiscount] = useState(0);
  const [giftCodeCartMsg, setGiftCodeCartMsg] = useState('');
  const [giftCodeCartLoading, setGiftCodeCartLoading] = useState(false);

  // Gift Code - Instant
  const [giftCodeInstant, setGiftCodeInstant] = useState('');
  const [giftCodeInstantData, setGiftCodeInstantData] = useState<GiftCode | null>(null);
  const [giftCodeInstantDiscount, setGiftCodeInstantDiscount] = useState(0);
  const [giftCodeInstantMsg, setGiftCodeInstantMsg] = useState('');
  const [giftCodeInstantLoading, setGiftCodeInstantLoading] = useState(false);

  // Confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [confirmType, setConfirmType] = useState<ConfirmType>('instant');
  const [pendingSize, setPendingSize] = useState('');
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Post-order popups
  const [showScratchCard, setShowScratchCard] = useState(false);
  const [scratchPaidAmount, setScratchPaidAmount] = useState(0);
  const [scratchPercent, setScratchPercent] = useState(10);
  const [showBonusPopup, setShowBonusPopup] = useState(false);
  const [earnedBonusAmount, setEarnedBonusAmount] = useState(0);
  const [newWalletTotal, setNewWalletTotal] = useState(0);
  const [bonusPaidAmount, setBonusPaidAmount] = useState(0);
  const [currentOTP, setCurrentOTP] = useState('');

  // Selected product for instant order
  const [selectedProduct, setSelectedProduct] = useState<CatProduct | null>(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [showInstantOrderModal, setShowInstantOrderModal] = useState(false);

  const clearCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const cancelOrderConfirmation = useCallback(() => {
    clearCountdown();
    setShowConfirmModal(false);
    setCountdown(5);
  }, [clearCountdown]);

  // Load wallet on mount
  useEffect(() => {
    syncWalletFromSupabase().then(() => setWalletBalance(getWalletBalance()));
  }, []);

  useEffect(() => {
    return () => clearCountdown();
  }, [clearCountdown]);

  const handleApplyGiftCodeCart = useCallback(async () => {
    if (!giftCodeCart.trim()) return;
    setGiftCodeCartLoading(true);
    setGiftCodeCartMsg('');
    const result = await validateGiftCode(giftCodeCart.trim(), cartTotal, customerUserPhone ?? '');
    setGiftCodeCartLoading(false);
    if (result.valid && result.codeData) {
      setGiftCodeCartDiscount(result.discount);
      setGiftCodeCartData(result.codeData);
      setGiftCodeCartMsg(result.message);
    } else {
      setGiftCodeCartDiscount(0);
      setGiftCodeCartData(null);
      setGiftCodeCartMsg(result.message);
    }
  }, [giftCodeCart, cartTotal, customerUserPhone]);

  const handleApplyGiftCodeInstant = useCallback(async (price: number) => {
    if (!giftCodeInstant.trim()) return;
    setGiftCodeInstantLoading(true);
    setGiftCodeInstantMsg('');
    const result = await validateGiftCode(giftCodeInstant.trim(), price, customerUserPhone ?? '');
    setGiftCodeInstantLoading(false);
    if (result.valid && result.codeData) {
      setGiftCodeInstantDiscount(result.discount);
      setGiftCodeInstantData(result.codeData);
      setGiftCodeInstantMsg(result.message);
    } else {
      setGiftCodeInstantDiscount(0);
      setGiftCodeInstantData(null);
      setGiftCodeInstantMsg(result.message);
    }
  }, [giftCodeInstant, customerUserPhone]);

  const executeInstantOrder = useCallback(async (size: string) => {
    const orderId = 'ORD-' + Date.now();
    const basePrice = selectedProduct?.price ?? 0;
    const bonus = applyBonusInstant ? calcApplicableBonus(walletBalance, basePrice) : 0;
    const paid = basePrice - bonus - giftCodeInstantDiscount;

    const earned = await updateWalletAfterOrder(paid, bonus, orderId);
    const walletTotal = getWalletBalance();

    if (giftCodeInstantData) {
      await redeemGiftCode(giftCodeInstantData.id, customerUserPhone ?? '', giftCodeInstantData.used_count, giftCodeInstantData.used_by ?? []);
    }

    const otp = generateOTP();

    let addressToUse = savedAddress;
    if (!addressToUse) {
      try {
        const saved = localStorage.getItem('userAddress');
        if (saved) addressToUse = JSON.parse(saved);
      } catch { /* ignore */ }
    }

    if (selectedProduct && addressToUse) {
      const normalizedPhone = normalizePhone(addressToUse.phone);
      const orderData = {
        id: orderId,
        customer_name: addressToUse.fullName,
        customer_phone: normalizedPhone,
        customer_address: addressToUse.address,
        customer_city: addressToUse.city,
        customer_pincode: addressToUse.pincode,
        customer_lat: addressToUse.lat,
        customer_lng: addressToUse.lng,
        items: [{ ...selectedProduct, quantity: 1, size }],
        total_amount: basePrice,
        wallet_discount: bonus,
        final_amount: Math.max(0, paid),
        payment_method: 'COD',
        status: 'pending',
        order_type: 'instant',
      };
      const saveResult = await saveOrder(orderData);
      if (!saveResult.success) {
        console.error('[Order] Save failed:', saveResult.error);
        return;
      }
      await saveDeliveryOTP(orderId, otp);
    }

    trackWithAttribution('Purchase', {
      content_ids: [String(selectedProduct?.id)],
      content_type: 'product',
      value: paid,
      currency: 'INR',
      num_items: 1,
      order_type: 'instant',
    });

    try {
      localStorage.setItem('currentOrder', JSON.stringify({
        id: orderId,
        product: selectedProduct ? { ...selectedProduct, image: selectedProduct.image?.substring(0, 300) ?? '', images: [] } : null,
        size,
        address: savedAddress,
        orderDate: new Date().toISOString(),
        status: 'confirmed',
      }));
    } catch { /* ignore */ }

    setShowConfirmModal(false);
    setShowInstantOrderModal(false);
    setSelectedProduct(null);
    playOrderSuccessSound();
    setCurrentOTP(otp);
    const pct = paid < 500 ? 7 : 10;
    setScratchPaidAmount(paid);
    setScratchPercent(pct);
    setShowScratchCard(true);
    setEarnedBonusAmount(earned);
    setNewWalletTotal(walletTotal);
    setBonusPaidAmount(paid);
  }, [selectedProduct, savedAddress, applyBonusInstant, walletBalance, giftCodeInstantDiscount, giftCodeInstantData, customerUserPhone]);

  const executeCartOrder = useCallback(async () => {
    const bonus = applyBonusCart ? calcApplicableBonus(walletBalance, cartTotal) : 0;
    const paid = Math.max(0, cartTotal - bonus - giftCodeCartDiscount);
    const orderId = 'ORD-' + Date.now();

    const earned = await updateWalletAfterOrder(paid, bonus, orderId);
    const walletTotal = getWalletBalance();

    if (giftCodeCartData) {
      await redeemGiftCode(giftCodeCartData.id, customerUserPhone ?? '', giftCodeCartData.used_count, giftCodeCartData.used_by ?? []);
    }

    const otp = generateOTP();

    let addressToUse = savedAddress;
    if (!addressToUse) {
      try {
        const saved = localStorage.getItem('userAddress');
        if (saved) addressToUse = JSON.parse(saved);
      } catch { /* ignore */ }
    }

    if (addressToUse) {
      const normalizedPhone = normalizePhone(addressToUse.phone);
      const orderData = {
        id: orderId,
        customer_name: addressToUse.fullName,
        customer_phone: normalizedPhone,
        customer_address: addressToUse.address,
        customer_city: addressToUse.city,
        customer_pincode: addressToUse.pincode,
        customer_lat: addressToUse.lat,
        customer_lng: addressToUse.lng,
        items: cart,
        total_amount: cartTotal,
        wallet_discount: bonus,
        final_amount: Math.max(0, paid),
        payment_method: 'COD',
        status: 'pending',
        order_type: 'cart',
      };
      const saveResult = await saveOrder(orderData);
      if (!saveResult.success) {
        console.error('[Order] Cart save failed:', saveResult.error);
        return;
      }
      await saveDeliveryOTP(orderId, otp);
    }

    trackWithAttribution('Purchase', {
      content_ids: cart.map(item => String(item.id)),
      content_type: 'product',
      value: paid,
      currency: 'INR',
      num_items: cart.length,
      order_type: 'cart',
    });

    try {
      localStorage.setItem('currentOrder', JSON.stringify({
        id: orderId,
        items: cart.map(item => ({ ...item, image: item.image?.substring(0, 300) ?? '' })),
        address: savedAddress,
        paymentMethod: 'Cash on Delivery',
        total: paid,
        orderDate: new Date().toISOString(),
        status: 'confirmed',
      }));
    } catch { /* ignore */ }

    setShowConfirmModal(false);
    setIsCartOpen(false);
    clearCart();
    playOrderSuccessSound();
    setCurrentOTP(otp);
    const pct = paid < 500 ? 7 : 10;
    setScratchPaidAmount(paid);
    setScratchPercent(pct);
    setShowScratchCard(true);
    setEarnedBonusAmount(earned);
    setNewWalletTotal(walletTotal);
    setBonusPaidAmount(paid);
  }, [cart, savedAddress, cartTotal, applyBonusCart, walletBalance, giftCodeCartDiscount, giftCodeCartData, customerUserPhone, clearCart, setIsCartOpen]);

  const executeInstantOrderRef = useRef(executeInstantOrder);
  const executeCartOrderRef = useRef(executeCartOrder);
  useEffect(() => { executeInstantOrderRef.current = executeInstantOrder; }, [executeInstantOrder]);
  useEffect(() => { executeCartOrderRef.current = executeCartOrder; }, [executeCartOrder]);

  const startConfirmation = useCallback((type: ConfirmType, size = '') => {
    setConfirmType(type);
    setPendingSize(size);
    setCountdown(5);
    setShowConfirmModal(true);
    clearCountdown();
    let remaining = 5;
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setCountdown(remaining);
      if (remaining <= 0) {
        clearCountdown();
        if (type === 'instant') {
          executeInstantOrderRef.current(size);
        } else {
          executeCartOrderRef.current();
        }
      }
    }, 1000);
  }, [clearCountdown]);

  const confirmInstantOrder = useCallback((size: string) => {
    setShowInstantOrderModal(false);
    startConfirmation('instant', size);
  }, [startConfirmation]);

  const handleBonusPopupClose = useCallback(() => {
    setShowBonusPopup(false);
    navigate('/profile');
  }, [navigate]);

  return {
    // Wallet
    walletBalance, setWalletBalance,
    applyBonusCart, setApplyBonusCart,
    applyBonusInstant, setApplyBonusInstant,
    // Gift Code Cart
    giftCodeCart, setGiftCodeCart,
    giftCodeCartData, setGiftCodeCartData,
    giftCodeCartDiscount, setGiftCodeCartDiscount,
    giftCodeCartMsg, setGiftCodeCartMsg,
    giftCodeCartLoading,
    handleApplyGiftCodeCart,
    // Gift Code Instant
    giftCodeInstant, setGiftCodeInstant,
    giftCodeInstantData, setGiftCodeInstantData,
    giftCodeInstantDiscount, setGiftCodeInstantDiscount,
    giftCodeInstantMsg, setGiftCodeInstantMsg,
    giftCodeInstantLoading,
    handleApplyGiftCodeInstant,
    // Confirmation
    showConfirmModal, setShowConfirmModal,
    countdown, confirmType, pendingSize,
    cancelOrderConfirmation,
    startConfirmation,
    confirmInstantOrder,
    executeInstantOrderRef,
    executeCartOrderRef,
    clearCountdown,
    // Product selection
    selectedProduct, setSelectedProduct,
    selectedSize, setSelectedSize,
    showInstantOrderModal, setShowInstantOrderModal,
    // Post-order
    showScratchCard, setShowScratchCard,
    scratchPaidAmount, scratchPercent,
    showBonusPopup, setShowBonusPopup,
    earnedBonusAmount, setEarnedBonusAmount,
    newWalletTotal, setNewWalletTotal,
    bonusPaidAmount,
    currentOTP,
    handleBonusPopupClose,
  };
}
